import {LinkedDocumentInfo, RepoMetadata} from "@/pb/api/devplan/types/integrations";
import {StateCreator} from "zustand/vanilla";
import {ProviderType} from "@/app/api/integrations/connect/types";
import {
  integrationRefreshAPI,
  integrationsAPI,
  repoMetaAPI,
  repoMetasAPI,
  repoRescanAPI,
  websiteRescanAPI
} from "@/app/apiPaths";
import {
  GetIntegrationsResponse,
  ListRepoMetadataResponse,
  RescanRepoRequest, RescanSiteRequest,
  UpdateRepoMetadataRequest,
  UpdateRepoMetadataResponse
} from "@/pb/api/devplan/services/web/company";
import {jr} from "@/hooks/queries/response-handler";
import {queryClient} from "@/hooks/queries/queryClient";
import {userKeys} from "@/hooks/queries/queryKeys";

export type IntegrationDocumentKey = { companyId: number, documentId: string, provider: ProviderType }

export type IntegrationsState = {
  linkedDocuments: Record<string, LinkedDocumentInfo>;
  setLinkedDocument(info: LinkedDocumentInfo): void;
  setLinkedDocuments(infos: LinkedDocumentInfo[]): void;
  fetchLinkedDocuments(companyId: number): Promise<void>;

  repoMetadata: Record<number, Record<string, RepoMetadata>>;

  fetchRepoMetadata(companyId: number): Promise<void>;
  updateRepoMetadata(md: RepoMetadata): Promise<void>;
  rescanRepo(companyId: number, repoId: string): Promise<void>;
  rescanSite(companyId: number, url: string): Promise<void>;
  refreshIntegration(companyId: number, provider: ProviderType): Promise<void>;
}

export const createIntegrationsSlice: StateCreator<
  IntegrationsState,
  [],
  [],
  IntegrationsState
> = ((set, get) => ({
  linkedDocuments: {},
  setLinkedDocument: async info => set(s => ({...s, linkedDocuments: {...s.linkedDocuments, [info.documentId]: info}})),
  setLinkedDocuments: (infos) => set(s => {
    const linkedDocuments = {...s.linkedDocuments}
    infos.forEach(i => linkedDocuments[getDocKey(i)] = i)
    return {...s, linkedDocuments}
  }),
  fetchLinkedDocuments: async (companyId) => {
    const resp = await fetch(integrationsAPI(companyId))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    const {infos} = GetIntegrationsResponse.fromJSON(await resp.json())
    get().setLinkedDocuments(infos)
  },

  repoMetadata: {},
  fetchRepoMetadata: async companyId => fetch(repoMetasAPI(companyId))
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {metadata} = ListRepoMetadataResponse.fromJSON(js)
      const newMD = metadata.reduce((a, m) => ({...a, [m.id]: m}), {} as Record<string, RepoMetadata>)
      set(s => ({...s, repoMetadata: {...s.repoMetadata, [companyId]: newMD}}))
    }),
  updateRepoMetadata: async md => fetch(repoMetaAPI(md.companyId, md.id),
    {method: "POST", body: JSON.stringify(UpdateRepoMetadataRequest.toJSON({metadata: md}))})
    .then(jr)
    .then(js => {
      const {metadata} = UpdateRepoMetadataResponse.fromJSON(js)
      set(s => ({
          ...s,
          repoMetadata: {
            ...s.repoMetadata,
            [md.companyId]: {...s.repoMetadata[md.companyId], [metadata!.id]: metadata!}
          }
        }
      ))
    }),
  rescanRepo: async (companyId, repoId) => fetch(repoRescanAPI(companyId, repoId),
    {method: "POST", body: JSON.stringify(RescanRepoRequest.toJSON({}))})
    .then(jr)
    .then(async () => {
      await queryClient.invalidateQueries({queryKey: userKeys.limits()})
    }),
  rescanSite: async (companyId, url) => fetch(websiteRescanAPI(companyId),
    {method: "POST", body: JSON.stringify(RescanSiteRequest.toJSON({url}))})
    .then(jr)
    .then(async () => {
      await queryClient.invalidateQueries({queryKey: userKeys.limits()})
    }),
  refreshIntegration: async (companyId, provider) => fetch(integrationRefreshAPI(companyId, provider), {method: "POST"})
    .then(jr).then(() => {
      return
    }),
}))

export function getKey(key: IntegrationDocumentKey): string {
  return JSON.stringify(key)
}

export function getDocKey(info: LinkedDocumentInfo): string {
  const {companyId, documentId} = info;
  return getKey({companyId, documentId, provider: getProvider(info)})
}

function getProvider(info: LinkedDocumentInfo): ProviderType {
  switch (info.data?.$case) {
    case "linear":
      return 'linear';
    case "jira":
      return 'jira';
  }
  throw new Error(`Unexpected provider for document info ${info}`)
}